import { useCallback, useMemo, useState } from "react";
import { ConnectProto } from "@racoon/api";
import { useDateFnsLocale } from "@racoon/i18n";
import { format } from "date-fns";
import { fromTimestamp, getEnumName, toTimestamp } from "@racoon/lib";
import type { NotificationAuthor, NotificationItem } from "@racoon/ui";
import { auditLogsGrpc, useListAuditLogsQuery } from "../api/audit-logs";
import { useGetOrganizationQuery } from "../api/organization/organization.grpc";
import { useAccumulatedList } from "./use-accumulated-list";
import { useSelectedCompany } from "../store/company/hooks/useSelectedCompany";

// The server's maximum page size
const PAGE_SIZE = 100;
const WINDOW_MS = 24 * 60 * 60 * 1000;

/** The shape the shared notification panel reads. */
const toNotificationItem = (log: ConnectProto.AuditLog): NotificationItem => ({
  id: String(log.id),
  category: getEnumName(ConnectProto.AuditLogCategorySchema, log.category) ?? "",
  log_type: getEnumName(ConnectProto.AuditLogTypeSchema, log.logType),
  message: log.message,
  created_at: fromTimestamp(log.createdAt)?.toISOString() ?? "",
  // Keyed like `authors`. A system event (user 0, which arrives as the truthy "0") or a
  // user deleted since has no `user`, so no author
  user_id: log.user ? String(log.user.id) : undefined,
  fields: log.fields,
});

/** The selected company's audit log for the last day: the notification panel's feed. */
export function useNotifications() {
  const dateFnsLocale = useDateFnsLocale();
  const { selectedCompanyId } = useSelectedCompany();
  // The viewed org's own subscriptions: GetOrganization carries the settings of the org it
  // is asked for, while GetSetting reads the caller's org from the token. Same argument as
  // the session's lookup, so an admin viewing another org shares its cache entry
  const { data: organization } = useGetOrganizationQuery(
    { organizationId: BigInt(selectedCompanyId || 0) },
    { skip: !selectedCompanyId },
  );
  const logTypes = useMemo(
    () => organization?.organizationSettings?.notificationConfig?.logTypes ?? [],
    [organization],
  );

  // The window ends when the panel opens and stays put while it's open, so events arriving
  // meanwhile don't shift the pages; refetch moves it to now
  const [openedAt, setOpenedAt] = useState(() => new Date());
  const arg = useMemo(
    () => ({
      organizationId: BigInt(selectedCompanyId || 0),
      logTypes,
      timeFilter: {
        from: toTimestamp(new Date(openedAt.getTime() - WINDOW_MS)),
        to: toTimestamp(openedAt),
      },
    }),
    [selectedCompanyId, logTypes, openedAt],
  );

  const list = useAccumulatedList<ConnectProto.AuditLogList, ConnectProto.AuditLog>({
    useListQuery: useListAuditLogsQuery,
    listEndpoint: auditLogsGrpc.endpoints.listAuditLogs,
    arg,
    pageSize: PAGE_SIZE,
    filterKey: [selectedCompanyId, logTypes.join(","), openedAt.getTime()].join("|"),
    selectRows: (data) => data.auditLogs,
    selectTotal: (data) => data.listInfo?.totalItems,
    selectRowKey: (log) => String(log.id),
    // An organization subscribed to no event types gets an empty panel, as before
    skip: !selectedCompanyId || logTypes.length === 0,
  });

  // Already newest first
  const logs = useMemo(() => list.rows.map(toNotificationItem), [list.rows]);

  const authors = useMemo(() => {
    const byId: Record<string, NotificationAuthor> = {};
    list.rows.forEach((log) => {
      if (log.user) {
        byId[String(log.user.id)] = {
          id: String(log.user.id),
          first_name: log.user.firstName,
          last_name: log.user.lastName,
        };
      }
    });
    return byId;
  }, [list.rows]);

  const formatTime = (date: string | Date): string => format(new Date(date), "HH:mm");

  const formatDate = (date: string | Date): string =>
    format(new Date(date), "EEEE, d MMMM yyyy", { locale: dateFnsLocale });

  const refetch = useCallback(() => setOpenedAt(new Date()), []);

  return {
    logs,
    // The panel derives initials and circle colours from these
    authors,
    isLoading: list.isFetching && logs.length === 0,
    // One page up front; the panel asks for the next as its end scrolls into view
    loadNextPage: list.loadNextPage,
    refetch,
    formatTime,
    formatDate,
    // The server's count for the window, not only the loaded pages
    totalCount: list.totalItems,
  };
}
